import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { tenantsService, TenantView } from '../../services/tenants/tenants.service';
import { usersService, UserAdminView } from '../../services/users/users.service';
import {
    paymentTerminalsService,
    PaymentTerminalView,
} from '../../services/payment-terminals/payment-terminals.service';
import { subscribeAdminEvents, AdminEvent } from '../../services/admin-events/admin-events.service';
import { hardRefresh } from '../../utils/hard-refresh';
import { useAuth } from '../AuthProvider';
import { deliverDigitalMenuSettings } from '../../services/digital-menu/digital-menu-management.service';

// Architectural summary: Session-scoped provider that owns ONE EventSource for the entire SuperAdmin tab lifetime. The single /admin/stream connection multiplexes every admin-scope domain (tenants + users today). Replaces the legacy per-page subscribe/unsubscribe pattern AND the previous per-domain dual connection (which minted two identical tickets and opened two parallel streams to the same SuperAdmin shell). Initial GETs (tenants list + users list) fire once on mount; the merged SSE keeps both caches fresh thereafter. SuperAdmin-only — non-super-admin users open zero streams (enabled=false short-circuits the effect). Touched flows: TenantsPage / UsersPage consume via useAdminTenants() / useAdminUsers(); UsersPage derives its active-tenant chip list from the SAME tenants snapshot. If you add a new admin-scope domain (e.g. global audit log), extend the AdminEvent union in admin-events.service.ts + the routing switch below + a new context — DO NOT open a second EventSource.

type TenantsContextValue = {
    tenants: TenantView[];
    isLoading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
};

type UsersContextValue = {
    users: UserAdminView[];
    isLoading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
};

type PaymentTerminalsContextValue = {
    paymentTerminals: PaymentTerminalView[];
    isLoading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
};

type ApprovalRequestsEventsContextValue = {
    revision: number;
};

type DashboardEventsContextValue = {
    revision: number;
    lastTenantId: string | null;
};

const TenantsAdminContext = createContext<TenantsContextValue | null>(null);
const UsersAdminContext = createContext<UsersContextValue | null>(null);
const PaymentTerminalsAdminContext = createContext<PaymentTerminalsContextValue | null>(null);
const ApprovalRequestsEventsContext = createContext<ApprovalRequestsEventsContextValue | null>(null);
const DashboardEventsContext = createContext<DashboardEventsContextValue | null>(null);

// Architectural summary: AdminEventsProvider — mounts once inside the authenticated shell. Owns:
//   - tenants snapshot (initial GET /tenants + live SSE)
//   - users snapshot (initial GET /users/list + live SSE)
//   - one /admin/stream EventSource that multiplexes both event domains
// Non-super-admin users short-circuit to empty contexts and open zero connections.
export const AdminEventsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const enabled = user?.role === 'super_admin';

    const [tenants, setTenants] = useState<TenantView[]>([]);
    const [tenantsLoading, setTenantsLoading] = useState(false);
    const [tenantsError, setTenantsError] = useState<string | null>(null);

    const [users, setUsers] = useState<UserAdminView[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersError, setUsersError] = useState<string | null>(null);

    const [paymentTerminals, setPaymentTerminals] = useState<PaymentTerminalView[]>([]);
    const [paymentTerminalsLoading, setPaymentTerminalsLoading] = useState(false);
    const [paymentTerminalsError, setPaymentTerminalsError] = useState<string | null>(null);
    const [approvalRequestsRevision, setApprovalRequestsRevision] = useState(0);
    const [dashboardRevision, setDashboardRevision] = useState(0);
    const [dashboardLastTenantId, setDashboardLastTenantId] = useState<string | null>(null);

    // Architectural summary: Re-fetches the canonical tenants list. Called on mount and exposed through context as an escape hatch — under normal operation the merged SSE keeps the snapshot fresh and refresh() should never be needed. Touches tenantsService.list (GET /tenants).
    const refreshTenants = useCallback(async () => {
        if (!enabled) {
            return;
        }
        setTenantsLoading(true);
        setTenantsError(null);
        try {
            const data = await tenantsService.list();
            setTenants(data);
        } catch (loadError: unknown) {
            setTenantsError(loadError instanceof Error ? loadError.message : 'טעינת החברות נכשלה');
        } finally {
            setTenantsLoading(false);
        }
    }, [enabled]);

    // Architectural summary: Re-fetches the canonical users list. Same usage rules as refreshTenants — escape hatch only. Touches usersService.listOnly (GET /users/list).
    const refreshUsers = useCallback(async () => {
        if (!enabled) {
            return;
        }
        setUsersLoading(true);
        setUsersError(null);
        try {
            const data = await usersService.listOnly();
            setUsers(data);
        } catch (loadError: unknown) {
            setUsersError(loadError instanceof Error ? loadError.message : 'טעינת המשתמשים נכשלה');
        } finally {
            setUsersLoading(false);
        }
    }, [enabled]);

    // Architectural summary: Re-fetches the canonical payment-terminals snapshot. Same usage rules as refreshTenants — escape hatch only. Touches paymentTerminalsService.list (GET /payment-terminals).
    const refreshPaymentTerminals = useCallback(async () => {
        if (!enabled) {
            return;
        }
        setPaymentTerminalsLoading(true);
        setPaymentTerminalsError(null);
        try {
            const data = await paymentTerminalsService.list();
            setPaymentTerminals(data);
        } catch (loadError: unknown) {
            setPaymentTerminalsError(
                loadError instanceof Error ? loadError.message : 'טעינת המסופים נכשלה',
            );
        } finally {
            setPaymentTerminalsLoading(false);
        }
    }, [enabled]);

    // Architectural summary: Performs the initial super-admin snapshot load in
    // parallel (tenants + users) to minimize first-paint wait and avoid
    // sequential request latency stacking.
    const bootstrapAdminSnapshots = useCallback(async () => {
        if (!enabled) {
            return;
        }

        setTenantsLoading(true);
        setUsersLoading(true);
        setPaymentTerminalsLoading(true);
        setTenantsError(null);
        setUsersError(null);
        setPaymentTerminalsError(null);

        const [tenantsResult, usersResult, paymentTerminalsResult] = await Promise.allSettled([
            tenantsService.list(),
            usersService.listOnly(),
            paymentTerminalsService.list(),
        ]);

        if (tenantsResult.status === 'fulfilled') {
            setTenants(tenantsResult.value);
        } else {
            setTenantsError(
                tenantsResult.reason instanceof Error
                    ? tenantsResult.reason.message
                    : 'טעינת החברות נכשלה',
            );
        }

        if (usersResult.status === 'fulfilled') {
            setUsers(usersResult.value);
        } else {
            setUsersError(
                usersResult.reason instanceof Error
                    ? usersResult.reason.message
                    : 'טעינת המשתמשים נכשלה',
            );
        }

        if (paymentTerminalsResult.status === 'fulfilled') {
            setPaymentTerminals(paymentTerminalsResult.value);
        } else {
            setPaymentTerminalsError(
                paymentTerminalsResult.reason instanceof Error
                    ? paymentTerminalsResult.reason.message
                    : 'טעינת המסופים נכשלה',
            );
        }

        setTenantsLoading(false);
        setUsersLoading(false);
        setPaymentTerminalsLoading(false);
    }, [enabled]);

    useEffect(() => {
        if (!enabled) {
            return;
        }
        void bootstrapAdminSnapshots();
    }, [bootstrapAdminSnapshots, enabled]);

    // Architectural summary: Single multiplexed admin SSE subscription. Routes every event by `event.type` prefix into the correct domain reducer. Adding a new admin-scope domain only requires adding a case here + extending the AdminEvent union in admin-events.service.ts. DO NOT add a second subscribeAdminEvents call — that resurrects the two-EventSource bug.
    useEffect(() => {
        if (!enabled || typeof window.EventSource !== 'function') {
            return;
        }
        const unsubscribe = subscribeAdminEvents((event: AdminEvent) => {
            switch (event.type) {
                case 'digital-menu.updated': {
                    deliverDigitalMenuSettings(event.tenantId, event.settings);
                    return;
                }
                // Super admins hold no tenant POS stream, so this is their only deploy signal.
                case 'deployment.refresh-requested': {
                    void hardRefresh();
                    return;
                }
                case 'tenant.upserted': {
                    const incoming = event.tenant as TenantView;
                    setTenants((previous) => {
                        let found = false;
                        const next = previous.map((row) => {
                            if (row.id === incoming.id) {
                                found = true;
                                return incoming;
                            }
                            return row;
                        });
                        if (!found) {
                            next.unshift(incoming);
                        }
                        return next;
                    });
                    return;
                }
                case 'tenant.active-changed': {
                    setTenants((previous) =>
                        previous.map((row) =>
                            row.tenantId === event.tenantId
                                ? { ...row, isActive: event.isActive }
                                : row,
                        ),
                    );
                    return;
                }
                case 'user.upserted': {
                    const incoming = event.user as UserAdminView;
                    setUsers((previous) => {
                        let found = false;
                        const next = previous.map((row) => {
                            if (row.id === incoming.id) {
                                found = true;
                                return incoming;
                            }
                            return row;
                        });
                        if (!found) {
                            next.unshift(incoming);
                        }
                        return next;
                    });
                    return;
                }
                case 'user.deactivated': {
                    setUsers((previous) =>
                        previous.map((row) =>
                            row.id === event.userId ? { ...row, isActive: false } : row,
                        ),
                    );
                    return;
                }
                case 'payment-terminal.upserted': {
                    const incoming = event.terminal as PaymentTerminalView;
                    setPaymentTerminals((previous) => {
                        let found = false;
                        const next = previous.map((row) => {
                            if (row.id === incoming.id) {
                                found = true;
                                return incoming;
                            }
                            return row;
                        });
                        if (!found) {
                            next.unshift(incoming);
                        }
                        return next;
                    });
                    return;
                }
                case 'payment-terminal.active-changed': {
                    setPaymentTerminals((previous) =>
                        previous.map((row) =>
                            row.id === event.terminalId
                                ? { ...row, isActive: event.isActive }
                                : row,
                        ),
                    );
                    return;
                }
                case 'approval-request.changed': {
                    setApprovalRequestsRevision((previous) => previous + 1);
                    return;
                }
                case 'dashboard.changed': {
                    setDashboardLastTenantId(
                        typeof event.tenantId === 'string' && event.tenantId.trim().length > 0
                            ? event.tenantId
                            : null,
                    );
                    setDashboardRevision((previous) => previous + 1);
                    return;
                }
            }
        });
        return () => {
            unsubscribe();
        };
    }, [enabled]);

    const tenantsValue = useMemo<TenantsContextValue>(
        () => ({
            tenants,
            isLoading: tenantsLoading,
            error: tenantsError,
            refresh: refreshTenants,
        }),
        [tenants, tenantsLoading, tenantsError, refreshTenants],
    );

    const usersValue = useMemo<UsersContextValue>(
        () => ({
            users,
            isLoading: usersLoading,
            error: usersError,
            refresh: refreshUsers,
        }),
        [users, usersLoading, usersError, refreshUsers],
    );

    const paymentTerminalsValue = useMemo<PaymentTerminalsContextValue>(
        () => ({
            paymentTerminals,
            isLoading: paymentTerminalsLoading,
            error: paymentTerminalsError,
            refresh: refreshPaymentTerminals,
        }),
        [paymentTerminals, paymentTerminalsLoading, paymentTerminalsError, refreshPaymentTerminals],
    );

    const approvalRequestsEventsValue = useMemo<ApprovalRequestsEventsContextValue>(
        () => ({ revision: approvalRequestsRevision }),
        [approvalRequestsRevision],
    );

    const dashboardEventsValue = useMemo<DashboardEventsContextValue>(
        () => ({ revision: dashboardRevision, lastTenantId: dashboardLastTenantId }),
        [dashboardLastTenantId, dashboardRevision],
    );

    // Stash refs so we can detect (and warn in dev) double-mounts of the provider that would defeat the one-connection guarantee.
    const mountedRef = useRef(false);
    useEffect(() => {
        if (mountedRef.current) {
            // eslint-disable-next-line no-console
            console.warn(
                'AdminEventsProvider mounted more than once in the same tree — the /admin/stream EventSource will open twice. Mount it exactly once at the top of AuthenticatedRoutes.',
            );
        }
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    return (
        <TenantsAdminContext.Provider value={tenantsValue}>
            <UsersAdminContext.Provider value={usersValue}>
                <PaymentTerminalsAdminContext.Provider value={paymentTerminalsValue}>
                    <ApprovalRequestsEventsContext.Provider value={approvalRequestsEventsValue}>
                        <DashboardEventsContext.Provider value={dashboardEventsValue}>
                            {children}
                        </DashboardEventsContext.Provider>
                    </ApprovalRequestsEventsContext.Provider>
                </PaymentTerminalsAdminContext.Provider>
            </UsersAdminContext.Provider>
        </TenantsAdminContext.Provider>
    );
};

// Architectural summary: Page-level hook for SuperAdmin tenants data. Returns a stable snapshot maintained live by the provider's multiplexed admin SSE. Pages MUST NOT open their own EventSource — use this hook.
export function useAdminTenants(): TenantsContextValue {
    const ctx = useContext(TenantsAdminContext);
    if (!ctx) {
        throw new Error('useAdminTenants must be used within AdminEventsProvider');
    }
    return ctx;
}

// Architectural summary: Page-level hook for SuperAdmin users data. Returns a stable snapshot maintained live by the provider's multiplexed admin SSE. Pages MUST NOT open their own EventSource — use this hook.
export function useAdminUsers(): UsersContextValue {
    const ctx = useContext(UsersAdminContext);
    if (!ctx) {
        throw new Error('useAdminUsers must be used within AdminEventsProvider');
    }
    return ctx;
}

// Architectural summary: Page-level hook for SuperAdmin payment-terminals data. Returns a stable snapshot maintained live by the provider's multiplexed admin SSE. Pages MUST NOT open their own EventSource — use this hook.
export function useAdminPaymentTerminals(): PaymentTerminalsContextValue {
    const ctx = useContext(PaymentTerminalsAdminContext);
    if (!ctx) {
        throw new Error('useAdminPaymentTerminals must be used within AdminEventsProvider');
    }
    return ctx;
}

export function useAdminApprovalRequestsEvents(): ApprovalRequestsEventsContextValue {
    const ctx = useContext(ApprovalRequestsEventsContext);
    if (!ctx) {
        throw new Error('useAdminApprovalRequestsEvents must be used within AdminEventsProvider');
    }
    return ctx;
}

export function useAdminDashboardEvents(): DashboardEventsContextValue {
    const ctx = useContext(DashboardEventsContext);
    if (!ctx) {
        throw new Error('useAdminDashboardEvents must be used within AdminEventsProvider');
    }
    return ctx;
}
